/**
 * Отраслевые пресеты — ТЗ §3.
 *
 * Значения выверены по прототипу `pronto_booking.html` (массив PRESETS): в нём все 80 значений
 * уже сверялись с таблицей ТЗ §3, и расхождение между кодом и прототипом было бы новым дефектом.
 *
 * Пресет — это ТОЛЬКО значения по умолчанию. При создании бизнеса они КОПИРУЮТСЯ в строку
 * `businesses` (ТЗ §3): позднее изменение пресета не должно менять правила у живых организаций.
 * Никакой код не имеет права читать пресет для уже созданного бизнеса — только его собственные
 * поля. Единственное исключение — экран онбординга, где пресет ещё только подставляется.
 */

/** Значение `businesses.type` и ключ пресета. Совпадает с enum BusinessType в Prisma. */
export type BusinessType =
  | 'barbershop'
  | 'beauty'
  | 'dental'
  | 'massage'
  | 'autoservice'
  | 'tutor'
  | 'vet'
  | 'grooming'
  | 'doctor';

/**
 * Постоянный бесплатный тариф. ТЗ §3: низкочековым массовым отраслям Free нужен, чтобы завести
 * массу, высокочековым он каннибализирует платящих. `light` — облегчённый Free салона красоты.
 */
export type FreeTier = 'yes' | 'light' | 'no';

/** Ключ i18n для отраслевого названия специалиста (ТЗ §3, «Терминология по отраслям»). */
export type SpecialistTermKey = 'termUsta' | 'termDoc' | 'termMass' | 'termTeach' | 'termGroom';

export interface IndustryPreset {
  /** Ключ пресета. Совпадает с `type`, кроме исторических id прототипа — см. PRESET_LEGACY_ID. */
  readonly type: BusinessType;
  readonly nameRu: string;
  readonly nameUz: string;
  /** Группа фильтра в каталоге. */
  readonly group: 'beauty' | 'health' | 'auto' | 'edu' | 'pets';
  /** Шаг сетки слотов, мин → `businesses.slot_step_min`. */
  readonly slotStepMin: number;
  /** Окно безопасной отмены, ч → `businesses.cancel_window_hours`. */
  readonly cancelWindowHours: number;
  /** Максимум активных записей клиента → `businesses.max_active_bookings`. Считается глобально (ТЗ §8). */
  readonly maxActiveBookings: number;
  /** Cooldown между записями, мин → `businesses.cooldown_minutes`. Тоже глобальный (ТЗ §8). */
  readonly cooldownMinutes: number;
  /** Срок локального бана, дней → `businesses.local_ban_days` → `blacklists.expires_at`. */
  readonly localBanDays: number;
  /** Длительность триала, дней → `businesses.trial_ends_at`. */
  readonly trialDays: number;
  readonly freeTier: FreeTier;
  /** Цена тарифа СОЛО, сум/мес. */
  readonly priceSoloSum: number;
  /** Цена тарифа МУЛЬТИ Pro+, сум/мес. */
  readonly priceMultiSum: number;
  readonly specialistTerm: SpecialistTermKey;
}

/**
 * Интервалы напоминаний по умолчанию, часы до визита → `businesses.reminder_lead_hours`.
 * ТЗ §19, расхождение №1: из трёх значений в исходных документах выбрано 2 часа, потому что
 * оно совпадает с окном безопасной отмены большинства пресетов.
 */
export const DEFAULT_REMINDER_LEAD_HOURS: readonly number[] = [24, 2];

/** Часовой пояс по умолчанию: продукт делается под Узбекистан. */
export const DEFAULT_TIMEZONE = 'Asia/Tashkent';

export const INDUSTRY_PRESETS: readonly IndustryPreset[] = [
  {
    type: 'barbershop',
    /**
     * Просто «Барбершоп», как в ТЗ §3. Слово «парикмахерская» убрано отсюда 10 сентября 2026
     * и перенесено в `beauty`: женская парикмахерская выбирала по нему барбершоп и получала
     * окно отмены 2 часа на окрашивание длительностью 3–5 часов. Промахивался при этом
     * не код, а владелец бизнеса — но результат тот же, что в правиле 7 AGENTS.md.
     */
    nameRu: 'Барбершоп',
    nameUz: 'Barbershop',
    group: 'beauty',
    slotStepMin: 30,
    cancelWindowHours: 2,
    maxActiveBookings: 3,
    cooldownMinutes: 10,
    localBanDays: 60,
    trialDays: 30,
    freeTier: 'yes',
    priceSoloSum: 59_000,
    priceMultiSum: 179_000,
    specialistTerm: 'termUsta',
  },
  {
    type: 'beauty',
    /** Женский зал живёт здесь: правила подобраны под его длительности (см. `barbershop`). */
    nameRu: 'Салон красоты, парикмахерская',
    nameUz: 'Goʻzallik saloni, sartaroshxona',
    group: 'beauty',
    slotStepMin: 30,
    /**
     * 4 часа, а не 3. Три часа были единственным значением вне общей шкалы (у массажа,
     * автосервиса, репетиторов, ветклиники, груминга и частного врача — 4) и при этом меньше
     * длительности услуги, ради которой пресет существует: сложное окрашивание идёт 3–5 часов.
     * Сутки как у стоматологии — перебор: салон с несколькими мастерами частично закрывает
     * освободившееся время короткими услугами.
     */
    cancelWindowHours: 4,
    maxActiveBookings: 3,
    cooldownMinutes: 10,
    localBanDays: 60,
    trialDays: 30,
    freeTier: 'light',
    priceSoloSum: 59_000,
    priceMultiSum: 179_000,
    specialistTerm: 'termUsta',
  },
  {
    type: 'dental',
    nameRu: 'Стоматология',
    nameUz: 'Stomatologiya',
    group: 'health',
    slotStepMin: 30,
    // 24 часа, а не 2: визит на 250–900k сум за два часа не перепродать (ТЗ §3).
    cancelWindowHours: 24,
    maxActiveBookings: 2,
    cooldownMinutes: 15,
    localBanDays: 90,
    trialDays: 30,
    freeTier: 'no',
    priceSoloSum: 199_000,
    priceMultiSum: 449_000,
    specialistTerm: 'termDoc',
  },
  {
    type: 'massage',
    nameRu: 'Массаж и СПА',
    nameUz: 'Massaj va SPA',
    group: 'health',
    slotStepMin: 30,
    cancelWindowHours: 4,
    maxActiveBookings: 2,
    cooldownMinutes: 10,
    localBanDays: 60,
    trialDays: 30,
    freeTier: 'no',
    priceSoloSum: 99_000,
    priceMultiSum: 249_000,
    specialistTerm: 'termMass',
  },
  {
    type: 'autoservice',
    nameRu: 'Автосервис',
    nameUz: 'Avtoservis',
    group: 'auto',
    slotStepMin: 60,
    cancelWindowHours: 4,
    maxActiveBookings: 2,
    cooldownMinutes: 15,
    localBanDays: 60,
    trialDays: 30,
    freeTier: 'no',
    priceSoloSum: 99_000,
    priceMultiSum: 249_000,
    specialistTerm: 'termUsta',
  },
  {
    type: 'tutor',
    nameRu: 'Репетиторы',
    nameUz: 'Repetitorlar',
    group: 'edu',
    slotStepMin: 30,
    cancelWindowHours: 4,
    maxActiveBookings: 5,
    cooldownMinutes: 10,
    localBanDays: 30,
    trialDays: 30,
    freeTier: 'yes',
    priceSoloSum: 59_000,
    priceMultiSum: 179_000,
    specialistTerm: 'termTeach',
  },
  {
    type: 'vet',
    nameRu: 'Ветклиника',
    nameUz: 'Vetklinika',
    group: 'pets',
    slotStepMin: 15,
    cancelWindowHours: 4,
    maxActiveBookings: 3,
    cooldownMinutes: 10,
    localBanDays: 60,
    // 30 дней, а не 14: ценность здесь — реактивация напоминанием о вакцинации,
    // на двух неделях она не проявляется (ТЗ §3).
    trialDays: 30,
    freeTier: 'no',
    priceSoloSum: 199_000,
    priceMultiSum: 449_000,
    specialistTerm: 'termDoc',
  },
  {
    type: 'grooming',
    nameRu: 'Груминг',
    nameUz: 'Gruming',
    group: 'pets',
    slotStepMin: 30,
    cancelWindowHours: 4,
    maxActiveBookings: 2,
    cooldownMinutes: 10,
    localBanDays: 60,
    trialDays: 30,
    freeTier: 'no',
    priceSoloSum: 59_000,
    priceMultiSum: 179_000,
    specialistTerm: 'termGroom',
  },
  {
    // Отрасли нет в таблице ТЗ §3: она добавлена позже отдельным решением (README, «Монетизация»)
    // и в финмодели не просчитана. Правила совпадают с массажем, цена ниже клиник, потому что
    // соло-врач платит из своего кармана. Это открытый вопрос, а не выверенная строка.
    type: 'doctor',
    nameRu: 'Частный врач',
    nameUz: 'Shaxsiy shifokor',
    group: 'health',
    slotStepMin: 30,
    cancelWindowHours: 4,
    maxActiveBookings: 2,
    cooldownMinutes: 10,
    localBanDays: 60,
    trialDays: 30,
    freeTier: 'no',
    priceSoloSum: 99_000,
    priceMultiSum: 249_000,
    specialistTerm: 'termDoc',
  },
];

const PRESET_BY_TYPE = new Map<BusinessType, IndustryPreset>(
  INDUSTRY_PRESETS.map((p) => [p.type, p]),
);

/**
 * Пресет по отрасли. Бросает на неизвестном типе, а не подставляет барбершоп молча:
 * тихий фолбэк здесь означал бы стоматологию с двухчасовым окном отмены.
 */
export function presetFor(type: BusinessType): IndustryPreset {
  const preset = PRESET_BY_TYPE.get(type);
  if (!preset) throw new Error(`Неизвестная отрасль: ${type}`);
  return preset;
}

/** Поля `businesses`, которые копируются из пресета при создании организации (ТЗ §3, §6.1). */
export interface BusinessPresetFields {
  slotStepMin: number;
  cancelWindowHours: number;
  maxActiveBookings: number;
  cooldownMinutes: number;
  localBanDays: number;
  reminderLeadHours: number[];
}

export function presetFieldsFor(type: BusinessType): BusinessPresetFields {
  const p = presetFor(type);
  return {
    slotStepMin: p.slotStepMin,
    cancelWindowHours: p.cancelWindowHours,
    maxActiveBookings: p.maxActiveBookings,
    cooldownMinutes: p.cooldownMinutes,
    localBanDays: p.localBanDays,
    reminderLeadHours: [...DEFAULT_REMINDER_LEAD_HOURS],
  };
}
